<template>
  <main ref="main">
    <img
      src="/images/backgrounds/olive-bg-3.png"
      ref="contactBg"
      class="absolute -bottom-[36px] left-0 -z-10 w-full select-none object-contain"
      alt="background"
    />
    <GridWrapper>
      <CompanyLineAnimations />
      <SectionSet is-first>
        <GridsTwelveGrid>
          <BreadCrumbs
            class="col-span-6 col-start-1 flex capitalize laptop:col-span-1 laptop:col-start-11 laptop:justify-end laptop:text-right"
          />
          <h1
            class="col-span-6 col-start-1 mt-1 capitalize laptop:col-span-7 laptop:col-start-5 laptop:mt-0 laptop:text-right"
          >
            {{ companyPage1Data?.companyPage?.data?.attributes?.hero_title }}
          </h1>
          <h2 class="col-span-2 col-start-2 mt-12 hidden laptop:mt-[56px] laptop:block">
            {{ companyPage1Data?.companyPage?.data?.attributes?.hero_introduction_title }}
          </h2>
          <div class="col-span-6 pt-8 italic laptop:col-span-7 laptop:col-start-2 laptop:pt-10">
            <AnimationsText
              :text="companyPage1Data?.companyPage?.data?.attributes?.hero_introduction_paragraph"
              type="lines"
            />
          </div>
          <CompanyCertificatesAwards class="col-span-6 mt-12 laptop:col-span-9 laptop:col-start-3 laptop:mt-[56px]" />
        </GridsTwelveGrid>
      </SectionSet>
      <SectionSet>
        <GridsTwelveGrid>
          <CompanyWhoWeAre class="col-span-6 laptop:col-span-8 laptop:col-start-3" />
        </GridsTwelveGrid>
      </SectionSet>
      <SectionSet class="laptop:mt-[66px]">
        <CompanyOurStoryDesktop class="z-10 flex" />
      </SectionSet>
    </GridWrapper>
    <div class="relative aspect-[153/77] h-full max-h-[830px] min-h-[560px] w-full laptop:aspect-[192/83]">
      <GridsOutterGrid>
        <GridsTwelveGrid class="z-10 col-span-1 col-start-2 mt-7 gap-y-10 laptop:gap-y-14">
          <template v-for="item in companyDataWithoutTheFirstThree" :key="item.text">
            <h4
              class="col-span-4 col-start-1 text-right text-lg text-white laptop:col-span-5 laptop:col-start-4 laptop:text-[20px]"
            >
              {{ item?.text }}
            </h4>
            <h4
              class="col-span-2 col-start-5 text-center text-lg text-white laptop:col-span-1 laptop:col-start-10 laptop:text-[20px]"
            >
              {{ item?.year }}
            </h4>
          </template>
        </GridsTwelveGrid>
        <VimeoCover
          class="absolute inset-0 col-span-3 col-start-1 h-full max-h-[830px] min-h-[462px] w-full object-cover"
        >
          <iframe
            title="vimeo-player"
            src="https://player.vimeo.com/video/889925386?h=b74b11fb38&background=1"
            width="640"
            height="360"
            frameborder="0"
            allowfullscreen
          ></iframe>
          <div class="absolute top-0 left-0 w-full h-full z-10 bg-ng800 bg-opacity-10" />
        </VimeoCover>
      </GridsOutterGrid>
    </div>
    <GridWrapper>
      <SectionSet class="mt-14">
        <CompanyIconsTitles />
      </SectionSet>
    </GridWrapper>
    <GridWrapper>
      <SectionSet>
        <GridsTwelveGrid>
          <CompanyOurFacilities class="col-span-6 laptop:col-span-8 laptop:col-start-3"> </CompanyOurFacilities>
        </GridsTwelveGrid>
      </SectionSet>
      <SectionSet>
        <GridsTwelveGrid>
          <CompanyPeopleEnvironment class="col-span-6 laptop:col-span-8 laptop:col-start-3"> </CompanyPeopleEnvironment>
        </GridsTwelveGrid>
      </SectionSet>
    </GridWrapper>
    <GridWrapper>
      <SectionSet class="mt-8 laptop:mt-10">
        <CompanyCirclesProgress />
      </SectionSet>
    </GridWrapper>

    <GridWrapper>
      <SectionSet>
        <GridsTwelveGrid>
          <EnvironmentWaterTreatment class="col-span-8 laptop:col-start-3" />
          <EnvironmentPhotovoltaicPanel class="col-span-8 mt-8 laptop:col-start-3 laptop:mt-[144px]" />
          <EnvironmentCompanyRecycling class="col-span-8 mt-8 laptop:col-start-3 laptop:mt-[144px]" />
        </GridsTwelveGrid>
      </SectionSet>
    </GridWrapper>
    <SectionSet isLast>
      <div class="flex flex-col">
        <NextBtn class="mx-auto" :text="$t('Contact us')" :to="localePath({ name: 'contact' })" />
      </div>
    </SectionSet>
  </main>
</template>
<script setup lang="ts">
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useWaitForPageTransitionEnd } from "~~/composables/waitPageTransition";
import { Companypage1DataKey, Companypage2DataKey } from "~/symbols/symbols";
const { locale, finalizePendingLocaleChange } = useI18n();
const localePath = useLocalePath();

await finalizePendingLocaleChange();

const { data: companyPage1Data } = await useAsyncGql({
  operation: "CompanyPage1",
  variables: { locale: locale.value },
  options: {
    lazy: false,
  },
});

provide(Companypage1DataKey, companyPage1Data);

const { data: companyPage2Data } = await useAsyncGql({
  operation: "CompanyPage2",
  variables: { locale: locale.value },
  options: {
    lazy: false,
  },
});

provide(Companypage2DataKey, companyPage2Data);

const companyDataWithoutTheFirstThree = computed(() => {
  return companyPage2Data?.value?.companyPage?.data?.attributes?.history_details.slice(3);
});

const pageTitle = ref<string | null>(null);

watch(
  companyPage1Data,
  (newVal) => {
    if (newVal) {
      if (newVal.companyPage?.data?.attributes?.seo?.metaTitle) {
        pageTitle.value = newVal.companyPage?.data?.attributes?.seo?.metaTitle;
      }
      useHead({
        title: () => pageTitle.value,
      });
      useServerSeoMeta({
        title: () => `${newVal.companyPage?.data?.attributes?.seo?.metaTitle}`,
        ogTitle: () => `${newVal.companyPage?.data?.attributes?.seo?.metaTitle}`,
        description: () => `${newVal.companyPage?.data?.attributes?.seo?.metaDescription}`,
        ogDescription: () => `${newVal.companyPage?.data?.attributes?.seo?.metaDescription}`,
        ogImage: () => `${newVal.companyPage?.data?.attributes?.seo?.metaImage?.data?.attributes?.url}`,
      });
    }
  },
  { immediate: true },
);

const path = ref<SVGPathElement | null>(null);
const main = ref<HTMLElement | null>(null);
const flowerSvg = ref<HTMLElement | null>(null);
const flowerPath = ref<SVGPathElement | null>(null);
const svgLine2 = ref<SVGPathElement | null>(null);
const svgLine2Path = ref<SVGPathElement | null>(null);
const flowerSvg2 = ref<HTMLElement | null>(null);
const flowerPath2 = ref<SVGPathElement | null>(null);
const flowerSvg3 = ref<HTMLElement | null>(null);
const flowerPath3 = ref<SVGPathElement | null>(null);
const svgLine3 = ref<SVGPathElement | null>(null);
const svgLine3Path = ref<SVGPathElement | null>(null);
const flowerSvg4 = ref<HTMLElement | null>(null);
const flowerPath4 = ref<SVGPathElement | null>(null);
const flowerPath5 = ref<SVGPathElement | null>(null);
const flowerPath6 = ref<SVGPathElement | null>(null);
const flowerPath7 = ref<SVGPathElement | null>(null);
const flowerPath8 = ref<SVGPathElement | null>(null);
const flowerPath9 = ref<SVGPathElement | null>(null);

let timeout: NodeJS.Timeout;

onMounted(() => {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    initAnimation();
  }, 0);
});

onBeforeUnmount(() => {
  // ScrollTrigger.killAll();
});

const { promise } = useWaitForPageTransitionEnd();
async function initAnimation() {
  await promise.value;

  if (path.value) {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: path.value,
          scrub: 2,
          start: "top top",
        },
      })
      .fromTo(
        path.value,
        {
          attr: {
            "stroke-dashoffset": 3344.74755859375,
            "stroke-dasharray": 3344.74755859375,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 3344.74755859375,
          },
          ease: "none",
        },
      );
  }
  if (flowerSvg.value) {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: flowerSvg.value,
          scrub: true,
          start: "top+=20px center",
          end: "bottom+=250px center",
        },
      })
      .fromTo(
        flowerPath.value,
        {
          attr: {
            "stroke-dashoffset": 1060.3875732421875,
            "stroke-dasharray": 1060.3875732421875,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 1060.3875732421875,
          },
          ease: "none",
        },
      );
  }
  if (svgLine2.value) {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: svgLine2.value,
          scrub: true,
          start: "top center",
          end: "bottom center",
        },
      })
      .fromTo(
        svgLine2Path.value,
        {
          attr: {
            "stroke-dashoffset": 755.6192626953125,
            "stroke-dasharray": 755.6192626953125,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 755.6192626953125,
          },
          ease: "none",
        },
      );
  }
  if (flowerSvg2.value) {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: flowerSvg2.value,
          scrub: true,
          start: "top+=170px center",
          end: "bottom+=170px center",
        },
      })
      .fromTo(
        flowerPath2.value,
        {
          attr: {
            "stroke-dashoffset": 1705.928588867,
            "stroke-dasharray": 1705.928588867,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 1705.928588867,
          },
          ease: "none",
        },
      );
  }
  if (flowerSvg3.value) {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: flowerSvg3.value,
          scrub: true,
          start: "top+=180px center",
          end: "bottom+=180px center",
        },
      })
      .fromTo(
        flowerPath3.value,
        {
          attr: {
            "stroke-dashoffset": 1097.59033203125,
            "stroke-dasharray": 1097.59033203125,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 1097.59033203125,
          },
          ease: "none",
        },
      );
  }
  if (svgLine3.value) {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: svgLine3.value,
          scrub: true,
          start: "top center",
          end: "bottom center",
        },
      })
      .fromTo(
        svgLine3Path.value,
        {
          attr: {
            "stroke-dashoffset": 3195.363525390625,
            "stroke-dasharray": 3195.363525390625,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 3195.363525390625,
          },
          ease: "none",
        },
      );
  }
  if (flowerSvg4.value) {
    gsap
      .timeline({
        scrollTrigger: {
          trigger: flowerSvg4.value,
          scrub: true,
          start: "top center",
          end: "bottom+=200px center",
        },
      })
      .fromTo(
        flowerPath4.value,
        {
          attr: {
            "stroke-dashoffset": 98.05416870117188,
            "stroke-dasharray": 98.05416870117188,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 98.05416870117188,
          },
          ease: "none",
        },
      )
      .fromTo(
        flowerPath5.value,
        {
          attr: {
            "stroke-dashoffset": 86.83973693847656,
            "stroke-dasharray": 86.83973693847656,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 86.83973693847656,
          },
          ease: "none",
        },
      )
      .fromTo(
        flowerPath6.value,
        {
          attr: {
            "stroke-dashoffset": 236.3463897705078,
            "stroke-dasharray": 236.3463897705078,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 236.3463897705078,
            fill: "#D9D9D9",
          },
          duration: 3,

          ease: "none",
        },
      )
      .fromTo(
        flowerPath7.value,
        {
          attr: {
            "stroke-dashoffset": 224.6712188720703,
            "stroke-dasharray": 224.6712188720703,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 224.6712188720703,
            fill: "#fff",
          },
          duration: 3,

          ease: "none",
        },
      )
      .fromTo(
        flowerPath8.value,
        {
          attr: {
            "stroke-dashoffset": 245.35609436035156,
            "stroke-dasharray": 245.35609436035156,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 245.35609436035156,
            fill: "#D9D9D9",
          },
          duration: 3,
          ease: "none",
        },
      )
      .fromTo(
        flowerPath9.value,
        {
          attr: {
            "stroke-dashoffset": 241.34767150878906,
            "stroke-dasharray": 241.34767150878906,
          },
        },
        {
          attr: {
            "stroke-dashoffset": 0,
            "stroke-dasharray": 241.34767150878906,
            fill: "#fff",
          },
          duration: 3,

          ease: "none",
        },
      );
  }
}
</script>
<style lang="scss" scoped></style>
